<template>
  <section class="limiter-content pa-3 pa-lg-0">
    <div class="padding-block"></div>

    <!-- Cabecalho no padrao das telas da area do credor/procurador -->
    <header class="dr-header">
      <span class="dr-eyebrow">
        <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
        {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
      </span>
      <h1 class="dr-title">Documentos <span>solicitados</span></h1>
      <p class="dr-subtitle">Documentos pedidos pela Administração Judicial. Envie cada um dentro do prazo da solicitação.</p>
    </header>

    <template v-if="user">
      <div v-if="isProcurator" class="dr-field">
        <procurator-creditor-selector v-model="selectedCreditorId" allow-pending></procurator-creditor-selector>
      </div>

      <div class="dr-list-head">
        <h2>Solicitações</h2>
        <span v-if="requests.length">{{ requests.length }} {{ requests.length === 1 ? "solicitação" : "solicitações" }}</span>
      </div>

      <v-progress-linear v-if="loading" indeterminate color="darkSecondary" class="mb-4"></v-progress-linear>

      <div v-if="!loading && !requests.length" class="dr-empty">
        <div class="dr-empty__icon"><v-icon size="34" color="darkSecondary">mdi-file-check-outline</v-icon></div>
        <div class="dr-empty__title">
          {{ isProcurator && !selectedCreditorId ? "Selecione o credor representado" : "Nenhuma solicitação de documentos" }}
        </div>
        <div class="dr-empty__text">
          {{ isProcurator && !selectedCreditorId ? "As solicitações aparecem depois que você escolhe o credor acima." : "Quando a Administração Judicial pedir documentos, eles aparecem aqui." }}
        </div>
      </div>

      <article v-for="request in requests" :key="request.id" class="dr-card" :class="{ 'dr-card--closed': request.is_closed }">
        <div class="dr-card__head">
          <div class="dr-card__icon"><v-icon color="darkSecondary">mdi-file-alert-outline</v-icon></div>
          <div class="dr-card__main">
            <div class="dr-card__title">Solicitação #{{ request.id }}<span v-if="request.client_name"> · {{ request.client_name }}</span></div>
            <div class="dr-muted">Enviada em {{ request.created_at | defaultDate('DD/MM/YYYY') }}</div>
          </div>
          <span class="dr-pill" :class="request.is_closed ? 'dr-pill--muted' : (allSent(request) ? 'dr-pill--ok' : 'dr-pill--warn')">
            <span class="dr-pill__dot"></span>
            <template v-if="request.is_closed">Prazo encerrado em {{ request.deadline | defaultDate('DD/MM/YYYY') }}</template>
            <template v-else-if="allSent(request)">Documentos enviados</template>
            <template v-else>Prazo: {{ request.deadline | defaultDate('DD/MM/YYYY') }}</template>
          </span>
        </div>

        <div v-if="request.message" class="dr-notice">
          <v-icon small color="#7e55ab">mdi-message-text-outline</v-icon>
          <div>
            <strong>Orientações da Administração Judicial</strong>
            <div class="dr-message">{{ request.message }}</div>
          </div>
        </div>

        <div v-if="request.is_closed && !allSent(request)" class="dr-notice dr-notice--danger">
          <v-icon small color="#b42318">mdi-alert-circle-outline</v-icon>
          <div>O prazo desta solicitação terminou e o envio foi encerrado. Em caso de dúvida, entre em contato com a Administração Judicial.</div>
        </div>

        <div v-if="request.justification" class="dr-notice dr-notice--warn">
          <v-icon small color="#a16207">mdi-message-alert-outline</v-icon>
          <div>
            <strong>Justificativa enviada</strong>
            <div v-html="request.justification"></div>
          </div>
        </div>

        <!-- Documentos pedidos -->
        <div class="dr-docs">
          <div v-for="doc in request.documents" :key="doc.id" class="dr-doc">
            <div class="dr-doc__icon" :class="doc.uploaded ? 'dr-doc__icon--ok' : (request.is_closed ? 'dr-doc__icon--muted' : 'dr-doc__icon--warn')">
              <v-icon small>{{ doc.uploaded ? "mdi-check" : (request.is_closed ? "mdi-lock-outline" : "mdi-clock-outline") }}</v-icon>
            </div>
            <div class="dr-doc__info">
              <div class="dr-doc__name">{{ doc.name }}</div>
              <div class="dr-doc__status">
                <span class="dr-pill" :class="doc.uploaded ? 'dr-pill--ok' : (request.is_closed ? 'dr-pill--muted' : 'dr-pill--warn')">{{ doc.tag }}</span>
                <span v-if="doc.uploaded_at" class="dr-muted">{{ doc.uploaded_at | defaultDate('DD/MM/YYYY HH:mm') }}</span>
              </div>
              <div v-if="doc.files && doc.files.length" class="dr-files">
                <div v-for="file in doc.files" :key="file.id" class="dr-file">
                  <v-icon x-small color="#7e55ab">mdi-paperclip</v-icon>
                  <a :href="file.url" target="_blank" rel="noopener">{{ file.name }}</a>
                  <span class="dr-muted">{{ file.uploaded_at | defaultDate('DD/MM HH:mm') }}</span>
                  <v-btn v-if="!request.is_closed && file.id" icon x-small color="error" :disabled="uploadingId !== null"
                    title="Remover arquivo" @click="removeFile(request, doc, file)">
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <div class="dr-doc__action">
              <template v-if="!request.is_closed">
                <input type="file" :ref="`file-${doc.id}`" class="d-none" :accept="acceptedDocumentTypes"
                  @change="onFileSelected(request, doc, $event)" />
                <v-btn :text="doc.uploaded" :depressed="!doc.uploaded" :color="doc.uploaded ? 'darkSecondary' : 'primary'"
                  :rounded="doc.uploaded" class="dr-btn" :class="{ 'dr-btn--text': doc.uploaded }"
                  :loading="uploadingId === doc.id" :disabled="uploadingId !== null" @click="pickFile(doc)">
                  <v-icon left small>mdi-upload</v-icon> {{ doc.uploaded ? 'Enviar outro arquivo' : 'Enviar arquivo' }}
                </v-btn>
                <div v-if="uploadingId === doc.id && uploadProgress !== null" class="dr-muted mt-1">
                  Enviando... {{ uploadProgress }}%
                </div>
              </template>
            </div>
          </div>
        </div>

        <div v-if="!request.is_closed && !allSent(request)" class="dr-card__footer">
          <v-btn text rounded color="darkSecondary" class="dr-action" @click="openJustify(request)">
            <v-icon left small>mdi-message-alert-outline</v-icon> Não consigo enviar algum documento
          </v-btn>
        </div>
      </article>
    </template>

    <login-required-notice v-else />

    <v-dialog v-model="justifyDialog.show" max-width="620" persistent>
      <v-card>
        <v-card-title>Justificativa</v-card-title>
        <v-card-text>
          <p>Explique à Administração Judicial por que algum documento não pode ser enviado.</p>
          <v-textarea v-model="justifyDialog.text" outlined rows="5" label="Justificativa" hide-details="auto"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="justifyDialog.loading" @click="justifyDialog.show = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="justifyDialog.loading" :disabled="!justifyDialog.text.trim()" @click="sendJustify">Enviar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row><whatsButton /></v-row>
    <v-row><topButton /></v-row>
  </section>
</template>

<script>
import axios from 'axios';
import topButton from '@/components/layout/GoToTopButton.vue';
import whatsButton from '@/components/layout/WhatsAppButton.vue';
import ProcuratorCreditorSelector from '@/components/ProcuratorCreditorSelector.vue';
import { needsChunkedUpload, chunkedUpload, validateDocumentFile, ACCEPTED_DOCUMENT_TYPES } from '@/common/util/chunked-upload';

// Solicitacoes de documentos feitas pela Central de Creditos ao credor.
// Independem de habilitacao/divergencia e fecham completamente apos o prazo.
export default {
  name: 'DocumentRequests',

  components: { topButton, whatsButton, ProcuratorCreditorSelector },

  data() {
    return {
      user: null,
      loading: false,
      requests: [],
      selectedCreditorId: null,
      uploadingId: null,
      uploadProgress: null,
      acceptedDocumentTypes: ACCEPTED_DOCUMENT_TYPES,
      justifyDialog: { show: false, request: null, text: '', loading: false },
    };
  },

  computed: {
    isProcurator() {
      return !!this.user && Number(this.user.type) === 2;
    },
  },

  watch: {
    selectedCreditorId() {
      this.load();
    },
  },

  created() {
    if (this.$root.user) {
      this.user = this.$root.user;
      this.selectedCreditorId = this.$route.query.creditor_user_id || null;
      this.load();
    }
  },

  methods: {
    allSent(request) {
      return (request.documents || []).every((doc) => doc.uploaded);
    },

    async load() {
      if (!this.user) return;
      if (this.isProcurator && !this.selectedCreditorId) {
        this.requests = [];
        return;
      }

      this.loading = true;
      try {
        const result = await axios.get('/api/document-requests', {
          params: { creditor_user_id: this.selectedCreditorId || undefined },
        });
        this.requests = Array.isArray(result.data.content) ? result.data.content : [];
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
      } finally {
        this.loading = false;
      }
    },

    pickFile(doc) {
      const input = this.$refs[`file-${doc.id}`];
      const element = Array.isArray(input) ? input[0] : input;
      if (element) element.click();
    },

    async removeFile(request, doc, file) {
      const proceed = await this.$dialog.confirm({
        title: 'Remover arquivo',
        text: `Remover o arquivo "${file.name}" de "${doc.name}"?`,
        actions: { false: 'Cancelar', true: 'Remover' },
      });
      if (!proceed) return;

      this.uploadingId = doc.id;
      try {
        await axios.delete(`/api/document-requests/${request.id}/documents/${doc.id}/files/${file.id}`, {
          params: this.selectedCreditorId ? { creditor_user_id: this.selectedCreditorId } : {},
        });
        this.$dialog.notify.success('Arquivo removido.', { position: 'top-right', timeout: 3000 });
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
      } finally {
        this.uploadingId = null;
      }
    },

    async onFileSelected(request, doc, event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;

      const fileError = validateDocumentFile(file);
      if (fileError) {
        this.$dialog.notify.error(fileError, { position: 'top-right', timeout: 5000 });
        return;
      }

      this.uploadingId = doc.id;
      this.uploadProgress = null;

      try {
        const formData = new FormData();
        if (this.selectedCreditorId) formData.append('creditor_user_id', this.selectedCreditorId);

        // Arquivo grande ou ZIP vai antes, em partes; os demais seguem no multipart.
        if (needsChunkedUpload(file)) {
          this.uploadProgress = 0;
          const uploaded = await chunkedUpload(file, {
            onProgress: (percent) => { this.uploadProgress = percent; },
          });
          formData.append('uploaded_key', uploaded.key);
          formData.append('uploaded_name', uploaded.name);
        } else {
          formData.append('file', file);
        }

        await axios.post(`/api/document-requests/${request.id}/documents/${doc.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        this.$dialog.notify.success('Documento enviado com sucesso.', { position: 'top-right', timeout: 3000 });
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
      } finally {
        this.uploadingId = null;
        this.uploadProgress = null;
      }
    },

    openJustify(request) {
      this.justifyDialog = { show: true, request, text: '', loading: false };
    },

    async sendJustify() {
      if (!this.justifyDialog.request || !this.justifyDialog.text.trim()) return;

      this.justifyDialog.loading = true;
      try {
        await axios.post(`/api/document-requests/${this.justifyDialog.request.id}/justify`, {
          justification: this.justifyDialog.text.trim(),
          creditor_user_id: this.selectedCreditorId || undefined,
        });
        this.$dialog.notify.success('Justificativa enviada.', { position: 'top-right', timeout: 3000 });
        this.justifyDialog.show = false;
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
      } finally {
        this.justifyDialog.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Mesmo padrao visual de "Meus credores", "Minha conta" e "Resumo de casos". */

.dr-header {
  padding: 32px 0 24px;
}

.dr-eyebrow {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #766386;
}

.dr-title {
  margin: 14px 0 10px;
  font-size: clamp(28px, 3.4vw, 40px);
  font-weight: 700;
  color: #25113c;
  letter-spacing: -0.035em;
  line-height: 1.2;
}

.dr-title span {
  color: #806697;
}

.dr-subtitle {
  margin: 0;
  color: #73677c;
  font-size: 14px;
}

.dr-muted {
  font-size: 13px;
  color: rgba(24, 0, 50, 0.55);
}

/* Seletor do credor representado com contorno lilas */
.dr-field {
  max-width: 520px;
}

.dr-field ::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(126, 85, 171, 0.45);
  border-radius: 10px;
}

.dr-field ::v-deep .v-text-field--outlined:hover fieldset {
  border-color: #7e55ab;
}

.dr-field ::v-deep .v-input__slot {
  border-radius: 10px !important;
}

.dr-field ::v-deep .v-alert {
  border-radius: 12px;
}

.dr-list-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 8px 0 12px;
}

.dr-list-head h2 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7e55ab;
}

.dr-list-head span {
  font-size: 12px;
  color: rgba(24, 0, 50, 0.5);
}

/* Cartao da solicitacao */
.dr-card {
  padding: 20px 22px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(24, 0, 50, 0.07);
  margin-bottom: 16px;
}

.dr-card--closed {
  background: #fcfbfd;
}

.dr-card__head {
  display: flex;
  align-items: center;
  gap: 12px 16px;
  flex-wrap: wrap;
}

.dr-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(120, 119, 229, 0.12);
  flex-shrink: 0;
}

.dr-card__main {
  flex: 1 1 260px;
  min-width: 0;
}

.dr-card__title {
  font-size: 15px;
  font-weight: 600;
  color: #180032;
}

.dr-card__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(24, 0, 50, 0.06);
}

/* Avisos dentro do cartao */
.dr-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  margin-top: 14px;
  background: rgba(120, 119, 229, 0.09);
  color: #2a1a40;
  font-size: 14px;
  line-height: 1.55;
}

.dr-notice .v-icon {
  margin-top: 2px;
}

.dr-notice--warn {
  background: #fff7e6;
}

.dr-notice--danger {
  background: #fdecea;
  color: #7a1b12;
}

.dr-message {
  white-space: pre-wrap;
  margin-top: 2px;
}

/* Documentos */
.dr-docs {
  margin-top: 14px;
}

.dr-doc {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
  border-top: 1px solid rgba(24, 0, 50, 0.06);
}

.dr-doc__icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dr-doc__icon .v-icon {
  color: inherit;
}

.dr-doc__icon--ok {
  background: #e7f6ee;
  color: #1e7b4f;
}

.dr-doc__icon--warn {
  background: #fff4dc;
  color: #a16207;
}

.dr-doc__icon--muted {
  background: #f1f0f4;
  color: #6b6475;
}

.dr-doc__name {
  font-size: 14px;
  font-weight: 600;
  color: #180032;
}

.dr-doc__status {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.dr-files {
  margin-top: 8px;
}

.dr-file {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
  line-height: 1.8;
}

.dr-file a {
  color: #7e55ab;
  text-decoration: none;
  overflow-wrap: anywhere;
}

.dr-file a:hover {
  text-decoration: underline;
}

.dr-doc__action {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

/* Botoes */
.dr-btn {
  height: 38px !important;
  padding: 0 16px !important;
  border-radius: 10px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.dr-btn--text {
  border-radius: 999px;
}

.dr-action {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
}

/* Pilulas de status */
.dr-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.dr-pill__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.dr-pill--ok {
  background: #e7f6ee;
  color: #1e7b4f;
}

.dr-pill--warn {
  background: #fff4dc;
  color: #a16207;
}

.dr-pill--muted {
  background: #f1f0f4;
  color: #6b6475;
}

/* Estado vazio */
.dr-empty {
  text-align: center;
  padding: 40px 16px 56px;
}

.dr-empty__icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(120, 119, 229, 0.12);
}

.dr-empty__title {
  font-size: 17px;
  font-weight: 600;
  color: #180032;
}

.dr-empty__text {
  margin-top: 6px;
  color: rgba(24, 0, 50, 0.6);
  font-size: 14px;
}

/* Celular */
@media (max-width: 760px) {
  .dr-doc {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .dr-doc__action {
    grid-column: 2;
    align-items: flex-start;
  }

  .dr-card__footer {
    justify-content: flex-start;
  }
}
</style>
